import React, { useCallback, useRef, useState } from 'react';

import { styled } from '@mui/material';

import { scrollToDropdown } from '../dropdown/dropdown-scroll';
import DropdownMenu from './dropdown';
import QuickNavDropdownButton from './dropdown-button';
import SubmitButton from './submit-button';

export const QuickNavigation = ({
  state,
  className = '',
  showHeadline = true,
  headline,
  submitAriaLabel,
  submitLabel,
  onSubmit,
  groupingFn,
  isDropdownOpen,
}: {
  state: {
    firstMenuOptions: { name: string; groupName?: string }[];
    secondMenuOptions: { name: string; groupName?: string }[];
    selectedFirstMenuOption: number | null;
    selectedSecondMenuOption: number | null;
    emitFirstItemSelection: (idx: number) => void;
    emitSecondItemSelection: (idx: number) => void;
    updateFirstMenuLabel: () => string;
    updateSecondMenuLabel: () => string;
  };
  className?: string;
  showHeadline?: boolean;
  headline?: string;
  submitAriaLabel: string;
  submitLabel: string;
  onSubmit: () => void;
  groupingFn: <T extends { name: string; groupName?: string }>(
    items: T[],
  ) => Record<string, (T & { index: number })[]>;
  isDropdownOpen?: (open: boolean) => void;
}) => {
  const firstMenuRef = useRef<HTMLButtonElement>(null);
  const secondMenuRef = useRef<HTMLButtonElement>(null);

  const [isFirstMenuOpen, setFirstMenuOpen] = useState(false);
  const [isSecondMenuOpen, setSecondMenuOpen] = useState(false);

  const scrollToFirstMenu = useRef(
    scrollToDropdown('first-menu-button', state.firstMenuOptions, {
      dropdownItemHeight: 32,
    }),
  );

  const scrollToSecondMenu = useRef(
    scrollToDropdown('second-menu-button', state.secondMenuOptions, {
      dropdownItemHeight: 32,
      dropdownPadding: 140,
    }),
  );

  const toggleFirstMenuOpen = useCallback(() => {
    if (!isFirstMenuOpen) {
      scrollToFirstMenu.current();
    }
    setFirstMenuOpen(!isFirstMenuOpen);
    if (isDropdownOpen) {
      isDropdownOpen(!isFirstMenuOpen);
    }
  }, [isFirstMenuOpen, isDropdownOpen]);

  const toggleSecondMenuOpen = useCallback(() => {
    if (!isSecondMenuOpen) {
      scrollToSecondMenu.current();
    }
    setSecondMenuOpen(!isSecondMenuOpen);
    if (isDropdownOpen) {
      isDropdownOpen(!isSecondMenuOpen);
    }
  }, [isSecondMenuOpen, isDropdownOpen]);

  const closeMenu = (callback: (value: boolean) => void, isOpen: boolean) => {
    callback(isOpen);
    if (isDropdownOpen) {
      isDropdownOpen(isOpen);
    }
  };

  const onSelectFirstItem = (idx: number) => {
    state.emitFirstItemSelection(idx);
    setFirstMenuOpen(false);
    setSecondMenuOpen(true);
  };

  const onSelectSecondItem = (idx: number) => {
    state.emitSecondItemSelection(idx);
    setSecondMenuOpen(false);
  };

  return (
    <>
      {showHeadline && <Title>{headline}</Title>}
      <QuickNavContainer className={`${className} mewa-quick-nav`}>
        <QuickNavMenuItem>
          <QuickNavDropdownButton
            id="first-menu-button"
            ref={firstMenuRef}
            onClick={toggleFirstMenuOpen}
            hasOpenMenu={isFirstMenuOpen}
          >
            {state.updateFirstMenuLabel()}
          </QuickNavDropdownButton>

          <DropdownMenu
            items={state.firstMenuOptions}
            open={isFirstMenuOpen}
            anchor={firstMenuRef.current}
            onClose={() => closeMenu(setFirstMenuOpen, false)}
            onSelect={onSelectFirstItem}
            name="first-menu"
            labelledby="first-menu-button"
            activeItem={state.selectedFirstMenuOption}
            groupingFn={groupingFn}
          />
        </QuickNavMenuItem>
        <QuickNavMenuItem>
          <QuickNavDropdownButton
            id="second-menu-button"
            ref={secondMenuRef}
            onClick={toggleSecondMenuOpen}
            hasOpenMenu={isSecondMenuOpen}
          >
            {state.updateSecondMenuLabel()}
          </QuickNavDropdownButton>
          <DropdownMenu
            items={state.secondMenuOptions}
            open={isSecondMenuOpen}
            anchor={secondMenuRef.current}
            onClose={() => closeMenu(setSecondMenuOpen, false)}
            onSelect={onSelectSecondItem}
            name="second-menu"
            labelledby="second-menu-button"
            activeItem={state.selectedSecondMenuOption}
            groupingFn={groupingFn}
          />
        </QuickNavMenuItem>
        <SubmitButton
          aria-label={submitAriaLabel}
          label={submitLabel}
          onClick={onSubmit}
        />
      </QuickNavContainer>
    </>
  );
};

const QuickNavContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  position: 'relative',
  gridTemplateColumns: 'auto',
  gridTemplateRows: 'auto auto auto',
  gridRowGap: 'var(--spacer-s)',
  gridColumnGap: 'var(--spacer-s)',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 48px',
    gridTemplateRows: 'auto auto',
  },

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '1fr 52px',
  },

  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: '1fr 57px',
  },
}));

const Title = styled('h2')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: '0 0 var(--spacer-s)',

  [theme.breakpoints.up('sm')]: {
    gridColumn: '1 / 3',
    marginBottom: '10px',
  },
}));

const QuickNavMenuItem = styled('div')({
  flexGrow: 1,
  position: 'relative',
});
